import { TERMS_LINKS } from '@avalara/bat-apps-shared/constants';
import { t } from '@avalara/bat-apps-shared/i18n';
import { Fragment } from 'react';

import ExternalLink from '../../external-link';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';

// suffix is plain text rendered right after this link (e.g. "supplemental terms" following a
// "Returns" link) — optional since most offers' terms links stand alone with no trailing text.
type TermsLink = { text: string; url: string; suffix?: string };

type Props = {
  termsChecked: boolean;
  onTermsChange: (checked: boolean) => void;
  separateAgreementChecked: boolean;
  onSeparateAgreementChange: (checked: boolean) => void;
  links: Array<TermsLink>;
  // i18n key (not raw text — see OfferClientDetails.termsAndConditionsSuffixKey) for text appended
  // after the offer's own configured links. Only rendered alongside real configured links: it
  // describes what THIS offer's links are, so it has no meaning for the generic fallback link below.
  suffixKey?: string;
};

// Mirrors buy-button.tsx's own getDisplayedTermsAndConditions — per OfferClientDetails.termsAndConditions
// (see types/offers.ts), an offer that leaves this unset is documented to fall back to Avalara's general
// terms rather than presenting no document at all.
const getDisplayedLinks = (links: Array<TermsLink>): Array<TermsLink> =>
  links.length > 0
    ? links
    : [{ text: t('client.general.avalara-service-terms-and-conditions'), url: TERMS_LINKS.general }];

// Skylab styles checkboxes via `input[type=checkbox]+label`, using ::before/::after on the LABEL
// (not the input) as the visible box — so the input and label must be true DOM siblings, not
// nested. Skylab's own stylesheet never sets `position: relative` on that label, so the label
// needs it here for its absolutely-positioned ::before/::after to anchor to itself rather than
// some other ancestor.
const TermsAndConditions = ({
  termsChecked,
  onTermsChange,
  separateAgreementChecked,
  onSeparateAgreementChange,
  links,
  suffixKey,
}: Props) => (
  <div data-ref='buy.order-review.terms' className='pad-bottom-md pad-top-md'>
    <h2 data-ref='buy.order-review.terms.heading'>{t('client.order-review.terms.heading')}</h2>
    <div>
      <input
        type='checkbox'
        id='order-review-terms-checkbox'
        data-ref='buy.order-review.terms.checkbox'
        checked={termsChecked}
        onChange={(e) => onTermsChange(e.target.checked)}
      />
      <label htmlFor='order-review-terms-checkbox' style={{ position: 'relative' }}>
        {t('client.order-review.terms.agree-to')}{' '}
        {getDisplayedLinks(links).map((link, i) => (
          <Fragment key={link.url}>
            {i > 0 && ' and the '}
            <ExternalLink
              href={link.url}
              alwaysOpenInNewTab
              className='button link margin-left-none'
              color='var(--color-accent-text)'
            >
              {link.text}
            </ExternalLink>
            {link.suffix && ` ${link.suffix}`}
          </Fragment>
        ))}
        {links.length > 0 && suffixKey && <> {t(suffixKey as TranslationKey)}</>}
      </label>
    </div>

    <div>
      <input
        type='checkbox'
        id='order-review-separate-agreement-checkbox'
        data-ref='buy.order-review.terms.separate-agreement-checkbox'
        checked={separateAgreementChecked}
        onChange={(e) => onSeparateAgreementChange(e.target.checked)}
      />
      <label htmlFor='order-review-separate-agreement-checkbox' style={{ position: 'relative' }}>
        {t('client.order-review.terms.separate-agreement-acknowledgement')}
      </label>
    </div>
  </div>
);

export default TermsAndConditions;
